import { createContext, useContext, useState, ReactNode, Fragment, isValidElement } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
  faCircleInfo,
  faCircleQuestion,
  faBan,
  faXmark,
  // Adicione outros ícones que precisar
} from '@fortawesome/pro-thin-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Button from '../Button';

// Expandir tipos
interface AlertOptions {
  title: string | ReactNode;
  message?: string | ReactNode;
  type?: 'success' | 'error' | 'warning' | 'info' | 'question' | 'danger';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  showCancel?: boolean;
  showClose?: boolean;
  icon?: React.ReactElement | string | (() => React.ReactElement) | false | null
}

export interface AlertContextType {
  show: (options: AlertOptions) => Promise<boolean>;
  hide: () => void;
}

export const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<AlertOptions>({
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    cancelText: 'Cancelar',
    showCancel: false,
    showClose: true,
  });
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const show = (alertOptions: AlertOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setOptions({
        type: 'info',
        confirmText: 'OK',
        cancelText: 'Cancelar',
        showCancel: false,
        ...alertOptions,
      });
      setResolvePromise(() => resolve);
      setIsOpen(true);
    });
  };

  const hide = () => {
    setIsOpen(false);
  };

  const handleConfirm = async () => {
    if (options.onConfirm) {
      await options.onConfirm();
    }
    if (resolvePromise) {
      resolvePromise(true);
    }
    hide();
  };

  const handleCancel = () => {
    if (options.onCancel) {
      options.onCancel();
    }
    if (resolvePromise) {
      resolvePromise(false);
    }
    hide();
  };

  const icons = {
    success: {
      icon: faCircleCheck,
      className: 'text-green-500',
    },
    error: {
      icon: faCircleXmark,
      className: 'text-red-500',
    },
    warning: {
      icon: faTriangleExclamation,
      className: 'text-yellow-500',
    },
    info: {
      icon: faCircleInfo,
      className: 'text-blue-500',
    },
    question: {
      icon: faCircleQuestion,
      className: 'text-purple-500',
    },
    danger: {
      icon: faBan,
      className: 'text-red-600',
    },
  };

  const buttonColors = {
    success: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
    error: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
    info: 'bg-blue hover:bg-blue/80 focus:ring-blue/50',
  };

  const currentIcon = { icon: options.icon, className: 'text-gray-700' }

  const CustomIcon = () => {
    if (typeof options.icon === 'function') return options.icon()

    if (isValidElement(options.icon)) return options.icon

    return null
  }

  return (
    <AlertContext.Provider value={{ show, hide }}>
      {children}

      {/* @ts-ignore - TypeScript compatibility issue with React 18 and HeadlessUI */}
      <Transition appear show={isOpen} as={Fragment}>
        {/* @ts-ignore - TypeScript compatibility issue with React 18 and HeadlessUI */}
        <Dialog as="div" className="relative z-50" onClose={handleCancel}>
          <Transition.Child
            // @ts-ignore - ts-expect-error - TypeScript compatibility issue with React 18 and HeadlessUI
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            {/* @ts-ignore - TypeScript compatibility issue with React 18 and HeadlessUI */}
            <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                // @ts-ignore - ts-expect-error - TypeScript compatibility issue with React 18 and HeadlessUI
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                {/* @ts-ignore - TypeScript compatibility issue with React 18 and HeadlessUI */}
                <Dialog.Panel className="w-full max-w-md mx-autotransform overflow-hidden rounded-2xl bg-satin-darkglass dark:text-white p-6 text-left align-middle shadow-xl transition-all">
                  {/* Botão fechar */}
                  {options.showClose && (
                    <Button
                      type="Link"
                      onClick={handleCancel}
                      className="absolute size-10  rounded-full *:top-4 right-4 hover:text-gray-600 transition-colors"
                    >
                      <FontAwesomeIcon icon={faXmark as IconProp} className="w-5 h-5" />
                    </Button>
                  )}

                  {/* Ícone */}
                  <div className="flex justify-center mb-4">
                    {!!CustomIcon ? (
                      <CustomIcon />
                    ) : (
                      <FontAwesomeIcon
                        icon={currentIcon.icon as IconProp}
                        className={`w-16 h-16 ${currentIcon.className}`}
                      />
                    )}
                  </div>

                  {/* Título */}
                  {typeof options.title === 'string' ? (
                    <Dialog.Title as="h3" className="text-lg font-semibold text-center mb-2">
                      {/* @ts-ignore - TypeScript compatibility issue with React 18 and HeadlessUI */}
                      {options.title}
                    </Dialog.Title>
                  ) : (
                    options.title
                  )}

                  {/* Mensagem */}
                  {options.message && (
                    <div className="mt-2">
                      <p className="text-sm text-secondary text-center">{options.message}</p>
                    </div>
                  )}

                  {/* Botões */}
                  <div className="mt-6 flex gap-3 justify-center">
                    {options.showCancel && (
                      <Button
                        type="Secondary"
                        className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
                        onClick={handleCancel}
                      >
                        {options.cancelText}
                      </Button>
                    )}
                    <Button
                      className={`${
                        buttonColors[options.type || 'info']
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors min-w-24`}
                      onClick={handleConfirm}
                    >
                      {options.confirmText}
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </AlertContext.Provider>
  );
};
